import PropTypes from "prop-types";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { CampaignSetupContext } from "../../../../../context/CampaignSetupContext";
import { useOutletKeywordGroups } from "../../../../../hooks/useOutletKeywordGroups";
import { useOutletMetadataSchema } from "../../../../../hooks/useOutletMetadataSchema";
import StoreSelector from "./StoreSelector";
import { buildStoreRuleData } from "./storeOutcomeUtils";

/**
 * Store-condition wizard body (no Dialog chrome) so it can be hosted inside the
 * shared "Add condition" modal (`AddConditionModal`) as the `storeGroup` branch:
 * The campaign's configured Store Groups scope the available rows. The selector
 * itself stays a single listing step so users can filter and choose stores from
 * any of those groups, matching the shared listing pattern used elsewhere.
 * On confirm it builds `ruleData` ({ keywords, store_configuration }) and hands it up.
 *
 * Columns and the persisted snapshot are driven by the OUTLET metadata schema
 * (`useOutletMetadataSchema`). The schema is required: while it loads we show a
 * spinner, and when it is absent/unconfigured the wizard blocks with a message
 * instead of falling back to a hardcoded field list.
 *
 */
export default function StoreConditionWizard({
  editData,
  onConditionAdd,
  onCancel,
  onBackToTypeSelect,
}) {
  const { t } = useTranslation("StoreOutcome");
  const { campaignState } = useContext(CampaignSetupContext);
  const allowedCategories =
    campaignState?.store_groups || campaignState?.storeGroups || [];
  const { stores, loading, error } = useOutletKeywordGroups({
    allowedCategories,
  });
  const {
    fields,
    isConfigured,
    loading: schemaLoading,
    error: schemaError,
  } = useOutletMetadataSchema();

  const handleConfirm = useCallback(
    (selection) => {
      // Thread the schema `fields` in so the persisted store_configuration snapshot
      // captures exactly the configured metadata paths (no hardcoded field list).
      onConditionAdd(buildStoreRuleData({ ...selection, fields }));
    },
    [onConditionAdd, fields]
  );

  // The metadata schema is required. Surface loading/unconfigured states before the
  // wizard so we never render columns off a hardcoded fallback.
  if (schemaLoading) {
    return (
      <p className="store-condition-selector__subtitle" data-testid="schema-loading">
        {t("LoadingSchema")}
      </p>
    );
  }
  if (schemaError || !isConfigured) {
    return (
      <p
        className="store-condition-selector__empty"
        data-testid="schema-unconfigured"
      >
        {t("SchemaUnconfigured")}
      </p>
    );
  }
  if (loading) {
    return (
      <p className="store-condition-selector__subtitle" data-testid="stores-loading">
        {t("LoadingStores")}
      </p>
    );
  }
  if (error) {
    return (
      <p className="store-condition-selector__empty" data-testid="stores-error">
        {t("LoadError")}
      </p>
    );
  }

  return (
    <StoreSelector
      stores={stores}
      initialSelection={editData}
      onConfirm={handleConfirm}
      onBack={onBackToTypeSelect}
      onCancel={onCancel}
    />
  );
}

StoreConditionWizard.propTypes = {
  onConditionAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  // When provided, Back returns to the shared condition-type picker. Omitted in
  // edit mode.
  onBackToTypeSelect: PropTypes.func,
  editData: PropTypes.shape({
    selectionType: PropTypes.string,
    category: PropTypes.string,
    storeIds: PropTypes.arrayOf(PropTypes.string),
  }),
};

StoreConditionWizard.defaultProps = {
  editData: null,
  onBackToTypeSelect: null,
};
